import React, { useState, useCallback } from 'react';
import './MainMenu.css';

function MainMenu({ onNavigate, soundEnabled, fadeOutMenuMusic, startGameplayMusic }) {
  const [showResolutionWarning, setShowResolutionWarning] = useState(false);
  const [winSize, setWinSize] = useState({ w: window.innerWidth, h: window.innerHeight });

  // Handle Play button click with sound sequence
  const handlePlayClick = useCallback(async () => {
    if (soundEnabled) {
      // Play impact sound first
      const impactSound = new Audio('/production-elements-impactor-e-188986.mp3');
      impactSound.volume = 0.3;
      impactSound.play().catch(() => {});
    }

    // Start fading out menu music immediately
    if (fadeOutMenuMusic) {
      fadeOutMenuMusic();
    }

    // Start gameplay music 3 seconds after Play click
    if (startGameplayMusic) {
      startGameplayMusic();
    }

    // Check if user has already seen the resolution warning
    const hasSeenWarning = localStorage.getItem('kickopp-resolution-warning-seen');
    if (hasSeenWarning === 'true') {
      // Skip warning and go directly to arena
      onNavigate('arena');
    } else {
      // Show warning for first-time players
      setShowResolutionWarning(true);
    }
  }, [soundEnabled, fadeOutMenuMusic, startGameplayMusic, onNavigate]);

  // Track live browser resolution
  React.useEffect(() => {
    const onResize = () => setWinSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Check if we should auto-start a match (from "New Match" button)
  React.useEffect(() => {
    const autoStart = localStorage.getItem('kickopp-auto-start-match');
    if (autoStart === 'true') {
      localStorage.removeItem('kickopp-auto-start-match');
      handlePlayClick();
    }
  }, [handlePlayClick]);

  // Hex tile positions on the 2160×1080 canvas (center 1080,540, radius 290px)
  // Angles: 240°/300° top, 180°/0° middle, 120°/60° bottom → 2-2-2 layout
  const HEX_POS = [
    { x: 935,  y: 289 }, // Play          — 240°
    { x: 1225, y: 289 }, // Challenge     — 300°
    { x: 790,  y: 540 }, // Player Cards  — 180°
    { x: 1370, y: 540 }, // Action Cards  — 0°
    { x: 935,  y: 791 }, // Kickoppedia   — 120°
    { x: 1225, y: 791 }, // What's Next   — 60°
  ];

  const menuButtons = [
    {
      id: 'play',
      label: 'Play',
      icon: null,
      iconSrc: '/icons/soccer-ball.svg',
      gradient: 'linear-gradient(160deg, #3b82f6 0%, #1d4ed8 100%)',
      glowColor: 'rgba(59,130,246,0.85)',
      action: handlePlayClick
    },
    {
      id: 'play-challenge',
      label: 'Challenge',
      icon: null,
      iconSrc: '/icons/trophy.svg',
      gradient: 'linear-gradient(160deg, #fbbf24 0%, #b45309 100%)',
      glowColor: 'rgba(251,191,36,0.85)',
      action: () => onNavigate('challenge')
    },
    {
      id: 'collection',
      label: 'Player Cards',
      icon: null,
      iconSrc: '/icons/player-cards.svg',
      gradient: 'linear-gradient(160deg, #a855f7 0%, #7e22ce 100%)',
      glowColor: 'rgba(168,85,247,0.85)',
      action: () => onNavigate('cards')
    },
    {
      id: 'action-cards',
      label: 'Action Cards',
      icon: null,
      iconSrc: '/icons/action-cards.svg',
      gradient: 'linear-gradient(160deg, #f97316 0%, #c2410c 100%)',
      glowColor: 'rgba(249,115,22,0.85)',
      action: () => onNavigate('action-cards')
    },
    {
      id: 'kickoppedia',
      label: 'Kickoppedia',
      icon: null,
      iconSrc: '/icons/kickoppedia.svg',
      gradient: 'linear-gradient(160deg, #10b981 0%, #065f46 100%)',
      glowColor: 'rgba(16,185,129,0.85)',
      action: () => onNavigate('kickoppedia')
    },
    {
      id: 'whatsnext',
      label: "What's Next",
      icon: null,
      iconSrc: '/icons/whats-next.svg',
      gradient: 'linear-gradient(160deg, #ef4444 0%, #991b1b 100%)',
      glowColor: 'rgba(239,68,68,0.85)',
      action: () => onNavigate('whatsnext')
    }
  ];

  return (
    <div className="main-menu">
      {/* Animated Background */}
      <div className="menu-background">
        <div className="bg-gradient"></div>
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/circuit-primary-left.svg`}
        className="circuit-img circuit-img-left"
        alt=""
        aria-hidden="true"
      />
      <img
        src={`${process.env.PUBLIC_URL}/circuit-primary.svg`}
        className="circuit-img circuit-img-right"
        alt=""
        aria-hidden="true"
      />

      {/* Hex Menu — center logo + 6 tiles in 2-2-2 circular layout */}
      <div className="hex-menu">
        {/* Center logo */}
        <div className="hex-center">
          <div className="logo-container">
            <div className="kickopp-logo">
              <div className="logo-k">
                <div className="k-vertical"></div>
                <div className="k-diagonals">
                  <div className="k-upper"></div>
                  <div className="k-lower"></div>
                </div>
              </div>
            </div>
            <h1 className="game-title">
              <span className="title-kick">KICK</span>
              <span className="title-opp">OPP</span>
            </h1>
            <p className="game-tagline">Football Strategy Card Game</p>
          </div>
        </div>

        {/* Six hex tiles */}
        {menuButtons.map((button, i) => (
          <div
            key={button.id}
            className="hex-tile-wrapper"
            style={{
              left: HEX_POS[i].x,
              top: HEX_POS[i].y,
              animationDelay: `${i * 0.35}s`,
              '--glow': button.glowColor,
            }}
          >
            <button
              className="hex-tile"
              onClick={button.action}
            >
              <div className="hex-tile-icon">
                {button.iconSrc
                  ? <img src={button.iconSrc} alt={button.label} style={{ width: '48px', height: '48px', display: 'block' }} />
                  : button.icon}
              </div>
              <div className="hex-tile-label">{button.label}</div>
            </button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="menu-footer">
        <p>Kickopp v0.4</p>
        <div className="res-strip">
          <span className="res-strip-icon">🖥</span>
          <span className="res-strip-size">{winSize.w} × {winSize.h}</span>
          {winSize.w >= 1920 && winSize.h >= 1080 ? (
            <span className="res-strip-badge res-strip-badge--ok">✓ Optimal</span>
          ) : (
            <span className="res-strip-badge res-strip-badge--warn">Best at 1920 × 1080</span>
          )}
        </div>
        <p className="res-strip-note">
          The game canvas is {winSize.w >= 1920 && winSize.h >= 1080
            ? 'filling your screen correctly.'
            : 'letterboxed — coloured margins appear when the browser window doesn\'t match the 1920×1080 target.'}
        </p>
      </div>

      {/* Resolution Warning Modal */}
      {showResolutionWarning && (
        <div className="resolution-warning-overlay">
          <div className="resolution-warning-modal">
            <div className="resolution-warning-header">
              <h2 className="resolution-warning-title">⚠️ Display Resolution Notice</h2>
            </div>
            <div className="resolution-warning-body">
              <p className="resolution-warning-message">
                <strong>Kickopp</strong> is designed and optimized for a display resolution of <strong>1920 x 1080</strong>.
              </p>
              <p className="resolution-warning-message">
                Playing at a different resolution on desktop may influence the game experience and visual layout.
              </p>

              <div className="resolution-warning-image-container">
                <p className="resolution-warning-image-label">Expected Game Layout (1920 x 1080):</p>
                <img
                  src="/optimal-resolution.png"
                  alt="Kickopp at optimal resolution 1920x1080"
                  className="resolution-warning-image"
                />
              </div>

              <p className="resolution-warning-note">
                💡 This will be improved in future versions to support more resolutions.
              </p>
            </div>
            <div className="resolution-warning-actions">
              <button
                className="resolution-warning-button cancel"
                onClick={() => setShowResolutionWarning(false)}
              >
                Go Back
              </button>
              <button
                className="resolution-warning-button confirm"
                onClick={() => {
                  // Mark that user has seen the warning
                  localStorage.setItem('kickopp-resolution-warning-seen', 'true');
                  setShowResolutionWarning(false);
                  onNavigate('arena');
                }}
              >
                Continue to Play
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainMenu;
